import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isAdmin } from "auth/tier";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService, dataPlaneService } from "../dataMapperServices";
import { sameId, type DataMapperProfile } from "../dataMapper.types";
import { bindSelectionToSignedInUser } from "../shared/editorSupport";
import {
  blockersFor,
  stepKeyForRoute,
  stepState,
  wizardStepsFor,
  WIZARD_ROUTE,
  WIZARD_STEPS,
  type WizardBlocker,
  type WizardPage,
  type WizardProgress,
  type WizardStepDef,
  type WizardStepKey,
  type WizardStepState,
} from "./wizardSteps";

/** Per-viewer convenience: whether the step instructions are folded away. */
export const WIZARD_HELP_KEY = "hq.dataMapper.wizardHelpCollapsed";

const readHelpCollapsed = (): boolean => {
  try {
    return globalThis.localStorage?.getItem(WIZARD_HELP_KEY) === "1";
  } catch {
    return false;
  }
};

/** A step as the stepper draws it. */
export interface WizardStepView {
  def: WizardStepDef;
  index: number;
  state: WizardStepState;
}

/** The live connection state a step screen may already know better than the wizard. */
interface ConnectionStore {
  connected?: boolean;
  statusLoaded?: boolean;
}

/**
 * The Data Mapper wizard's ViewStore: which step this tab is on, how far the
 * open profile has got, and Back / Next.
 *
 * It holds ids and flags only. The profile record stays in the
 * `data-mapper-profiles` ServiceStore; the source and target connections are
 * the data plane's, read with the same `status` calls the step screens use.
 *
 * Every Data Mapper route calls `enter()` from its onEnter (dataMapperRoutesList.ts),
 * so the stepper is current when a step opens. A plain class with
 * makeAutoObservable, registered as `dataMapperWizardViewStore`.
 */
class DataMapperWizardStore {
  rootStore: RootStore;

  /** What the last status call said about this tab's source and target. */
  sourceStatus = false;
  targetStatus = false;
  /** Folded instructions (remembered per browser). */
  helpCollapsed = readHelpCollapsed();

  private enterSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "enterSeq">(this, { rootStore: false, enterSeq: false });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get isAdmin(): boolean {
    return isAdmin(this.rootStore);
  }

  /** The open profile's record, from the profiles ServiceStore (null until it is loaded). */
  get profile(): DataMapperProfile | null {
    const id = this.selection?.selectedProfileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ?? []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  get isTitan(): boolean {
    return this.profile?.titanSource === true;
  }

  get currentRouteName(): string | null {
    const route = (this.rootStore as unknown as { routerStore?: { currentRoute?: { name?: string } | null } }).routerStore
      ?.currentRoute;
    return route?.name ?? null;
  }

  get currentKey(): WizardStepKey | null {
    return stepKeyForRoute(this.currentRouteName);
  }

  /**
   * The step screens' own view of the connection, while it has been read:
   * a Connect or Disconnect on the Source or Target page shows in the stepper
   * at once, before the wizard's next status call.
   */
  private liveConnection(storeKey: string, route: string, fallback: boolean): boolean {
    // Only while that screen is open: its store keeps its last answer after the route exits.
    if (this.currentRouteName !== route) return fallback;
    const store = (this.rootStore as unknown as Record<string, ConnectionStore | undefined>)[storeKey];
    return store?.statusLoaded ? store.connected === true : fallback;
  }

  get progress(): WizardProgress {
    const selection = this.selection;
    const hasProfile = selection?.hasProfile === true;
    return {
      hasProfile,
      sourceConnected:
        hasProfile && this.liveConnection("dataMapperSourceViewStore", WIZARD_ROUTE.source, this.sourceStatus),
      targetConnected:
        hasProfile && this.liveConnection("dataMapperTargetViewStore", WIZARD_ROUTE.target, this.targetStatus),
      hasSchema: hasProfile && !!selection?.selectedSchemaId,
      hasMappingConfig: hasProfile && !!selection?.selectedMappingConfigId,
    };
  }

  get steps(): WizardStepView[] {
    const current = this.currentKey;
    const progress = this.progress;
    return wizardStepsFor(this.isTitan).map((def, index) => ({
      def,
      index,
      state: stepState(def.key, current, progress, def.optional === true),
    }));
  }

  get currentIndex(): number {
    return this.steps.findIndex((s) => s.def.key === this.currentKey);
  }

  get currentStep(): WizardStepView | null {
    return this.steps[this.currentIndex] ?? null;
  }

  get previousStep(): WizardStepView | null {
    const i = this.currentIndex;
    return i > 0 ? this.steps[i - 1] : null;
  }

  get nextStep(): WizardStepView | null {
    const i = this.currentIndex;
    return i >= 0 && i < this.steps.length - 1 ? this.steps[i + 1] : null;
  }

  /** The current step's pages this person may open (admin-only ones hidden from everyone else). */
  get currentPages(): WizardPage[] {
    const step = this.currentStep;
    if (!step) return [];
    return step.def.pages.filter((p) => !p.adminOnly || this.isAdmin);
  }

  get blockers(): WizardBlocker[] {
    const key = this.currentKey;
    return key ? blockersFor(key, this.progress) : [];
  }

  /** Read where this tab stands: the open profile (for its Titan flag), and the source and target status. */
  async enter(): Promise<void> {
    bindSelectionToSignedInUser(this.rootStore);
    if (!this.canUse) return;
    const selection = this.selection;
    const seq = ++this.enterSeq;
    if (!selection?.hasProfile) {
      this.sourceStatus = false;
      this.targetStatus = false;
      return;
    }
    const profileId = selection.selectedProfileId as string;
    const profiles = dataMapperService(this.rootStore, "data-mapper-profiles");
    const source = dataPlaneService(this.rootStore, "data-mapper-data-source");
    const target = dataPlaneService(this.rootStore, "data-mapper-target-connection");
    const status = async (service: ReturnType<typeof dataPlaneService>): Promise<boolean> => {
      if (!service) return false;
      try {
        const answer = (await service.create({ action: "status", ...selection.dataPlaneQuery })) as { connected?: boolean } | null;
        return answer?.connected === true;
      } catch {
        // A refused or failed status call reads as "not connected"; the step screen says why.
        return false;
      }
    };
    const [sourceConnected, targetConnected] = await Promise.all([
      status(source),
      status(target),
      // The Titan flag decides the steps. A reload may not have the profile cached yet.
      this.profile || !profiles ? Promise.resolve() : Promise.resolve(profiles.get(profileId)).catch(() => undefined),
    ]);
    runInAction(() => {
      if (seq !== this.enterSeq || !sameId(this.selection?.selectedProfileId, profileId)) return;
      this.sourceStatus = sourceConnected;
      this.targetStatus = targetConnected;
    });
  }

  /** Open a step (its first page). A locked step opens the Profiles step instead. */
  goToStep(key: WizardStepKey) {
    const view = this.steps.find((s) => s.def.key === key);
    const target = view?.state === "locked" ? WIZARD_STEPS.profile : (view?.def ?? WIZARD_STEPS[key]);
    void this.goToRoute(target.pages[0].route);
  }

  goToRoute(route: string): Promise<void> | void {
    const router = (this.rootStore as unknown as { routerStore?: { goTo: (r: string) => Promise<void> } }).routerStore;
    return router?.goTo(route);
  }

  next() {
    const step = this.nextStep;
    if (step) this.goToStep(step.def.key);
  }

  back() {
    const step = this.previousStep;
    if (step) this.goToStep(step.def.key);
  }

  toggleHelp() {
    this.helpCollapsed = !this.helpCollapsed;
    try {
      globalThis.localStorage?.setItem(WIZARD_HELP_KEY, this.helpCollapsed ? "1" : "0");
    } catch {
      // Storage disabled: the choice holds for this page's life.
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.sourceStatus = false;
    this.targetStatus = false;
    this.helpCollapsed = readHelpCollapsed();
    this.enterSeq += 1;
  }
}

export default DataMapperWizardStore;
